// src/app/features/user/userSlice.js
import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import authServiceDefault, {
  authService as authServiceNamed,
} from "./userService";
import { toast } from "react-toastify";

/**
 * ✅ IMPORTANT NOTE
 * userService exports BOTH:
 *   - named export:  export const authService = {...}
 *   - default export: export default authService;
 *
 * To avoid mistakes, we create one "svc" reference:
 */
const svc = authServiceNamed || authServiceDefault;

/**
 * ✅ Small helper:
 * Some backends return { token }, others return { accessToken }.
 * Always normalize into one string.
 */
const pickToken = (obj) => {
  const t = obj?.token || obj?.accessToken || "";
  return typeof t === "string" ? t : "";
};

/**
 * ✅ Normalize auth payload
 * Supports:
 * - flat payload: { firstname, lastname, token, ... }
 * - nested payload: { user: {...}, token, ... }
 */
const normalizeAuthUser = (obj) => {
  const src =
    obj?.user && typeof obj.user === "object" ? { ...obj, ...obj.user } : obj || {};

  const token = pickToken(src);

  return {
    ...src,
    token,
    accessToken: token,
    firstname: String(src?.firstname || "").trim(),
    lastname: String(src?.lastname || "").trim(),
    email: String(src?.email || "").trim(),
    mobile: String(src?.mobile || "").trim(),
  };
};

/* =========================
   ✅ STORAGE KEYS
========================= */
const GUEST_CART_KEY = "kupto_guest_cart_v1";
const RETURN_TO_KEY = "kupto_returnTo";
const IDENTITY_KEY = "kupto_identity";
const IDENTITY_TYPE_KEY = "kupto_identity_type";

/* =========================
   ✅ SAFE AUTH STORAGE HELPERS
========================= */
const setCustomerStorage = (value) => {
  try {
    localStorage.setItem("customer", JSON.stringify(value || null));
    window.dispatchEvent(new Event("authUpdated"));
  } catch {}
};

const setTokenStorage = (token) => {
  try {
    if (token) localStorage.setItem("token", token);
    else localStorage.removeItem("token");
    window.dispatchEvent(new Event("authUpdated"));
  } catch {}
};

const clearClientAuthStorage = () => {
  try {
    localStorage.removeItem("customer");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  } catch {}

  try {
    sessionStorage.removeItem(RETURN_TO_KEY);
    sessionStorage.removeItem(IDENTITY_KEY);
    sessionStorage.removeItem(IDENTITY_TYPE_KEY);
  } catch {}

  try {
    if (window?.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  } catch {}

  try {
    window.dispatchEvent(new Event("authUpdated"));
  } catch {}
};

/* =========================
   ✅ GUEST CART (localStorage)
   - used for "guest checkout flow"
   - merged into user cart after login/google login
========================= */
const readGuestCart = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

const writeGuestCart = (list) => {
  const safe = Array.isArray(list) ? list : [];
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(safe));
  window.dispatchEvent(new Event("guestCartUpdated"));
};

const clearGuestCart = () => writeGuestCart([]);

/**
 * Map a guest cart item into the payload your backend expects for addToCart
 * (keeps print + upload meta too)
 */
const guestItemToCartPayload = (it) => {
  if (!it || !it.productId) return null;

  const payload = {
    productId: it.productId,
    quantity: Math.max(1, Number(it.quantity || it.qty || 1)),
    price: Number(it.price || it.unitPrice || 0),
  };

  // variants
  if (it.color) payload.color = it.color;
  if (it.size) payload.size = it.size;

  // print/upload meta
  if (Array.isArray(it.uploadedFiles) && it.uploadedFiles.length) {
    payload.uploadedFiles = it.uploadedFiles;
  }
  if (it.instruction) payload.instruction = String(it.instruction);

  if (it.printSide) payload.printSide = String(it.printSide);
  if (it.printUnitPrice !== undefined) {
    payload.printUnitPrice = Number(it.printUnitPrice || 0);
  }
  if (it.printKey) payload.printKey = String(it.printKey || "");
  if (it.printPricingTitle) {
    payload.printPricingTitle = String(it.printPricingTitle || "");
  }
  if (it.preparePriceOnce !== undefined) {
    payload.preparePriceOnce = Number(it.preparePriceOnce || 0);
  }
  if (it.preparePriceApplied !== undefined) {
    payload.preparePriceApplied = Boolean(it.preparePriceApplied);
  }

  // image hint (optional)
  const vImg = it?.variantImage || it?.img || it?.image || "";
  if (vImg) payload.variantImage = vImg;

  return payload;
};

/* =========================
   ✅ IDENTIFY (check email/phone exists)
   Backend endpoint: /user/identify
========================= */
export const identifyUser = createAsyncThunk(
  "auth/identify",
  async ({ identity, type }, thunkAPI) => {
    try {
      const res = await svc.identify({ identity, type });
      return res;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Identify failed");
    }
  }
);

/* =========================
   ✅ AUTH
========================= */
export const registerUser = createAsyncThunk(
  "auth/register",
  async (userData, thunkAPI) => {
    try {
      const response = await svc.register(userData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Registration failed");
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/login",
  async (userData, thunkAPI) => {
    try {
      const response = await svc.login(userData);
      const normalized = normalizeAuthUser(response);

      // ✅ store customer always
      setCustomerStorage(normalized);

      // ✅ only store token if it exists
      const t = pickToken(normalized);
      if (t) setTokenStorage(t);

      // ✅ merge guest cart ONLY if authenticated
      if (t) {
        const guest = readGuestCart();
        if (guest.length) {
          for (const it of guest) {
            const payload = guestItemToCartPayload(it);
            if (!payload) continue;

            // eslint-disable-next-line no-await-in-loop
            await thunkAPI.dispatch(addProdToCart(payload)).unwrap();
          }

          clearGuestCart();
        }

        // ✅ fetch fresh cart
        await thunkAPI.dispatch(getUserCart()).unwrap();
      }

      return normalized;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Login failed");
    }
  }
);

export const googleLoginUser = createAsyncThunk(
  "auth/google",
  async (payload, thunkAPI) => {
    try {
      const response = await svc.googleLogin(payload);

      // ✅ If profile is required, keep original shape for profile flow
      if (response?.profileRequired) {
        setCustomerStorage(response);
        return response;
      }

      const normalized = normalizeAuthUser(response);

      // ✅ store customer always
      setCustomerStorage(normalized);

      const t = pickToken(normalized);
      if (t) setTokenStorage(t);

      if (t) {
        const guest = readGuestCart();
        if (guest.length) {
          for (const it of guest) {
            const cartPayload = guestItemToCartPayload(it);
            if (!cartPayload) continue;

            // eslint-disable-next-line no-await-in-loop
            await thunkAPI.dispatch(addProdToCart(cartPayload)).unwrap();
          }

          clearGuestCart();
        }

        await thunkAPI.dispatch(getUserCart()).unwrap();
      }

      return normalized;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Google login failed");
    }
  }
);

export const completeGoogleProfileUser = createAsyncThunk(
  "auth/google/complete-profile",
  async (payload, thunkAPI) => {
    try {
      const response = await svc.completeGoogleProfile(payload);
      const normalized = normalizeAuthUser(response);

      setCustomerStorage(normalized);

      const t = pickToken(normalized);
      if (t) setTokenStorage(t);

      if (t) {
        const guest = readGuestCart();
        if (guest.length) {
          for (const it of guest) {
            const cartPayload = guestItemToCartPayload(it);
            if (!cartPayload) continue;

            // eslint-disable-next-line no-await-in-loop
            await thunkAPI.dispatch(addProdToCart(cartPayload)).unwrap();
          }

          clearGuestCart();
        }

        await thunkAPI.dispatch(getUserCart()).unwrap();
      }

      return normalized;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to complete Google profile"
      );
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, thunkAPI) => {
    try {
      const response = await svc.logout();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Logout failed");
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  "auth/refresh",
  async (_, thunkAPI) => {
    try {
      const res = await svc.refresh();
      return res;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Refresh failed");
    }
  }
);

/* =========================
   ✅ EMAIL VERIFICATION (USER CTRL)
========================= */
export const sendOtpCode = createAsyncThunk(
  "auth/otp/send",
  async ({ identity, type }, thunkAPI) => {
    try {
      const res = await svc.sendVerificationCode({ identity, type });
      return res;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Send code failed");
    }
  }
);

export const verifyOtpCode = createAsyncThunk(
  "auth/otp/verify",
  async ({ identity, type, code }, thunkAPI) => {
    try {
      const res = await svc.verifyCode({ identity, type, code });
      return res;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Verify code failed");
    }
  }
);

/* =========================
   ✅ WISHLIST
========================= */
export const getUserProductWishlist = createAsyncThunk(
  "user/wishlist",
  async (_, thunkAPI) => {
    try {
      const response = await svc.getUserWishlist();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Fetch wishlist failed");
    }
  }
);

export const removeWishlistProduct = createAsyncThunk(
  "user/wishlist/remove",
  async (productId, thunkAPI) => {
    try {
      const response = await svc.removeProductFromWishlist(productId);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Remove wishlist failed");
    }
  }
);

/* =========================
   ✅ CART
========================= */
export const addProdToCart = createAsyncThunk(
  "user/cart/add",
  async (cartData, thunkAPI) => {
    try {
      const response = await svc.addToCart(cartData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Add to cart failed");
    }
  }
);

export const getUserCart = createAsyncThunk(
  "user/cart/get",
  async (_, thunkAPI) => {
    try {
      const response = await svc.getCart();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Fetch cart failed");
    }
  }
);

export const deleteUserCart = createAsyncThunk(
  "user/cart/delete",
  async (_, thunkAPI) => {
    try {
      const response = await svc.emptyCart();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Empty cart failed");
    }
  }
);

export const deleteCartProduct = createAsyncThunk(
  "user/cart/product/delete",
  async (id, thunkAPI) => {
    try {
      const response = await svc.removeProductFromCart(id);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Delete cart item failed");
    }
  }
);

export const updateCartProduct = createAsyncThunk(
  "user/cart/product/update",
  async (cartDetail, thunkAPI) => {
    try {
      const response = await svc.updateProductFromCart(cartDetail);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Update cart failed");
    }
  }
);

/* =========================
   ✅ PROFILE
========================= */
export const updateProfile = createAsyncThunk(
  "user/profile/update",
  async (data, thunkAPI) => {
    try {
      const response = await svc.updateUser(data);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Update profile failed");
    }
  }
);

/* =========================
   ✅ PASSWORD RESET (EMAIL CODE FLOW)
========================= */
export const forgotPasswordTok = createAsyncThunk(
  "user/password/token",
  async (data, thunkAPI) => {
    try {
      const response = await svc.forgotPassCode(data);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Reset code failed");
    }
  }
);

export const verifyResetCodeTok = createAsyncThunk(
  "user/password/verify-code",
  async (data, thunkAPI) => {
    try {
      const response = await svc.verifyResetCode(data);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Verify reset code failed");
    }
  }
);

export const resetPassword = createAsyncThunk(
  "user/password/reset",
  async (data, thunkAPI) => {
    try {
      const response = await svc.resetPassCode(data);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Reset password failed");
    }
  }
);





// ✅ Keep old imports working
export const resetPasswordTok = resetPassword;

/* =========================
   ✅ ORDERS
========================= */
export const createAnOrder = createAsyncThunk(
  "user/cart/create-order",
  async (data, thunkAPI) => {
    try {
      const response = await svc.createOrder(data);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Create order failed");
    }
  }
);

export const getOrders = createAsyncThunk(
  "user/order/get",
  async (_, thunkAPI) => {
    try {
      const response = await svc.getUserOrders();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Fetch orders failed");
    }
  }
);

export const cancelMyOrder = createAsyncThunk(
  "user/order/cancel",
  async (orderId, thunkAPI) => {
    try {
      const response = await svc.cancelOrder(orderId);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Cancel order failed");
    }
  }
);

export const createDpoPaymentToken = createAsyncThunk(
  "user/dpo/create-token",
  async (orderId, thunkAPI) => {
    try {
      const response = await svc.createDpoToken(orderId);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Failed to start DPO payment");
    }
  }
);

export const verifyDpoPaymentToken = createAsyncThunk(
  "user/dpo/verify-token",
  async ({ token, orderId }, thunkAPI) => {
    try {
      const response = await svc.verifyDpoToken({ token, orderId });
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || "Failed to verify DPO payment");
    }
  }
);

export const resetState = createAction("Reset_all");

/* =========================
   ✅ INITIAL STATE
   IMPORTANT: use a function, not a frozen object
========================= */
const getInitialState = () => {
  let customer = null;
  let token = "";

  try {
    customer = localStorage.getItem("customer")
      ? normalizeAuthUser(JSON.parse(localStorage.getItem("customer")))
      : null;
  } catch {
    customer = null;
  }

  try {
    token = localStorage.getItem("token") || "";
  } catch {
    token = "";
  }

  return {
    user: customer,

    // identify
    identifyResult: null,
    identifyError: "",

    // email verification
    otpSendResult: null,
    otpVerifyResult: null,
    otpError: "",

    // google complete profile
    googleProfileCompleteResult: null,
    googleProfileCompleteError: "",

    // auth refresh
    accessToken: null,
    refreshError: "",

    // password reset flow
    forgotPassResult: null,
    verifyResetResult: null,
    passResetResult: null,
    passResetError: "",

    isError: false,
    isSuccess: false,
    isLoading: false,
    message: "",

    cartProduct: [],
    getOrderedProduct: [],
    wishlist: [],

    createdUser: null,
    updatedUser: null,

    deletedCartProduct: null,
    removedWishlistProduct: null,

    dpoTokenResult: null,
    dpoVerifyResult: null,
    dpoPaymentUrl: "",
    dpoError: "",

    createdOrder: null,
    cancelledOrder: null,

    token,
    pass: null,
  };
};

/* =========================
   ✅ SLICE
========================= */
const authSlice = createSlice({
  name: "auth",
  initialState: getInitialState(),
  reducers: {
    clearIdentifyState: (state) => {
      state.identifyResult = null;
      state.identifyError = "";
    },
    clearOtpState: (state) => {
      state.otpSendResult = null;
      state.otpVerifyResult = null;
      state.otpError = "";
    },
    clearPasswordResetState: (state) => {
      state.forgotPassResult = null;
      state.verifyResetResult = null;
      state.passResetResult = null;
      state.passResetError = "";
      state.pass = null;
    },
    clearGoogleCompleteProfileState: (state) => {
      state.googleProfileCompleteResult = null;
      state.googleProfileCompleteError = "";
    },
    clearDpoState: (state) => {
      state.dpoTokenResult = null;
      state.dpoVerifyResult = null;
      state.dpoPaymentUrl = "";
      state.dpoError = "";
    },
  },
  extraReducers: (builder) => {
    builder
      /* =========================
         IDENTIFY
      ========================= */
      .addCase(identifyUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.identifyError = "";
        state.identifyResult = null;
      })
      .addCase(identifyUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.identifyResult = action.payload;
      })
      .addCase(identifyUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.identifyError = action.payload || "Identify failed";
        toast.error(state.identifyError);
      })

      /* =========================
         REGISTER
      ========================= */
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.createdUser = action.payload;
        toast.info("User Created Successfully");
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        toast.error(state.message);
      })

      /* =========================
         LOGIN
      ========================= */
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        const normalized = normalizeAuthUser(action.payload);
        state.user = normalized;

        const t = pickToken(normalized);
        setCustomerStorage(normalized);

        if (t) {
          setTokenStorage(t);
          state.token = t;
          state.accessToken = t;
        }

        toast.info("User Logged in Successfully");

        state.cartProduct = action.payload?.cart || state.cartProduct || [];
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        toast.info("Invalid Credentials");
      })

      /* =========================
         GOOGLE LOGIN
      ========================= */
      .addCase(googleLoginUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(googleLoginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        if (action.payload?.profileRequired) {
          state.user = action.payload;
          setCustomerStorage(action.payload);
          return;
        }

        const normalized = normalizeAuthUser(action.payload);
        state.user = normalized;
        setCustomerStorage(normalized);

        const t = pickToken(normalized);
        if (t) {
          setTokenStorage(t);
          state.token = t;
          state.accessToken = t;
        }
      })
      .addCase(googleLoginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        toast.error(state.message || "Google login failed");
      })

      /* =========================
         GOOGLE COMPLETE PROFILE
      ========================= */
      .addCase(completeGoogleProfileUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.googleProfileCompleteError = "";
        state.googleProfileCompleteResult = null;
      })
      .addCase(completeGoogleProfileUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        const normalized = normalizeAuthUser(action.payload);

        state.googleProfileCompleteResult = normalized;
        state.user = normalized;

        setCustomerStorage(normalized);

        const t = pickToken(normalized);
        if (t) {
          setTokenStorage(t);
          state.token = t;
          state.accessToken = t;
        }

        toast.success("Profile completed successfully");
      })
      .addCase(completeGoogleProfileUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.googleProfileCompleteError =
          action.payload || "Failed to complete Google profile";
        state.message = state.googleProfileCompleteError;
        toast.error(state.googleProfileCompleteError);
      })

      /* =========================
         LOGOUT
      ========================= */
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, () => {
        clearClientAuthStorage();
        return getInitialState();
      })
      .addCase(logoutUser.rejected, (state, action) => {
        clearClientAuthStorage();

        const next = getInitialState();
        next.isError = true;
        next.isSuccess = false;
        next.isLoading = false;
        next.message = action.payload || "Logout failed";

        toast.error(next.message);
        return next;
      })

      /* =========================
         REFRESH TOKEN
      ========================= */
      .addCase(refreshAccessToken.pending, (state) => {
        state.isLoading = true;
        state.refreshError = "";
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        const t = pickToken(action.payload);
        state.accessToken = t || null;

        if (t) {
          state.token = t;
          setTokenStorage(t);

          try {
            const cRaw = localStorage.getItem("customer");
            if (cRaw) {
              const c = normalizeAuthUser(JSON.parse(cRaw));
              c.token = t;
              c.accessToken = t;
              setCustomerStorage(c);

              if (state.user && typeof state.user === "object") {
                state.user = { ...state.user, token: t, accessToken: t };
              }
            }
          } catch {}
        }
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.refreshError = action.payload || "Refresh failed";
      })

      /* =========================
         EMAIL VERIFICATION SEND
      ========================= */
      .addCase(sendOtpCode.pending, (state) => {
        state.isLoading = true;
        state.otpError = "";
        state.otpSendResult = null;
      })
      .addCase(sendOtpCode.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.otpSendResult = action.payload;
      })
      .addCase(sendOtpCode.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.otpError = action.payload || "Send code failed";
        toast.error(state.otpError);
      })

      /* =========================
         EMAIL VERIFICATION VERIFY
      ========================= */
      .addCase(verifyOtpCode.pending, (state) => {
        state.isLoading = true;
        state.otpError = "";
        state.otpVerifyResult = null;
      })
      .addCase(verifyOtpCode.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.otpVerifyResult = action.payload;
      })
      .addCase(verifyOtpCode.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.otpError = action.payload || "Verify code failed";
        toast.error(state.otpError);
      })

      /* =========================
         WISHLIST
      ========================= */
      .addCase(getUserProductWishlist.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserProductWishlist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.wishlist = action.payload;
      })
      .addCase(getUserProductWishlist.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
      })

      .addCase(removeWishlistProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeWishlistProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.removedWishlistProduct = action.payload;

        if (Array.isArray(state.wishlist?.wishlist)) {
          state.wishlist.wishlist = state.wishlist.wishlist.filter(
            (item) => item._id !== action.meta.arg
          );
        }

        toast.success("Product removed from wishlist");
      })
      .addCase(removeWishlistProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        toast.error(state.message || "Failed to remove wishlist item");
      })

      /* =========================
         CART
      ========================= */
      .addCase(addProdToCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addProdToCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        if (Array.isArray(action.payload)) {
          state.cartProduct = action.payload;
        } else if (Array.isArray(action.payload?.cart)) {
          state.cartProduct = action.payload.cart;
        } else if (action.payload && typeof action.payload === "object") {
          state.cartProduct = action.payload;
        }
      })
      .addCase(addProdToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        toast.error(state.message);
      })

      .addCase(getUserCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        const payload = action.payload;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.cart)
          ? payload.cart
          : [];
        state.cartProduct = list;
      })
      .addCase(getUserCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
      })

      .addCase(deleteUserCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteUserCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.deletedCartProduct = action.payload;
        state.cartProduct = [];
      })
      .addCase(deleteUserCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        toast.error(state.message || "Failed to clear cart");
      })

      .addCase(deleteCartProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteCartProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.deletedCartProduct = action.payload;
        toast.success("Product Deleted Successfully!");

        if (Array.isArray(state.cartProduct)) {
          state.cartProduct = state.cartProduct.filter(
            (item) => item?._id !== action.meta.arg
          );
        }
      })
      .addCase(deleteCartProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        toast.error(state.message);
      })

      .addCase(updateCartProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateCartProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        if (Array.isArray(state.cartProduct)) {
          const updated = action.payload;
          const id = updated?._id;
          if (id) {
            state.cartProduct = state.cartProduct.map((c) =>
              c?._id === id ? updated : c
            );
          }
        }
      })
      .addCase(updateCartProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        toast.error(state.message || "Failed to update cart");
      })

      /* =========================
         PROFILE UPDATE
      ========================= */
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        state.updatedUser = action.payload;

        if (state.user && typeof state.user === "object") {
          const merged = normalizeAuthUser({ ...state.user, ...action.payload });
          state.user = merged;
          setCustomerStorage(merged);
        }

        toast.success("Profile updated");
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        toast.error(state.message || "Failed to update profile");
      })

      /* =========================
         PASSWORD RESET - SEND CODE
      ========================= */
      .addCase(forgotPasswordTok.pending, (state) => {
        state.isLoading = true;
        state.passResetError = "";
        state.forgotPassResult = null;
      })
      .addCase(forgotPasswordTok.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        state.forgotPassResult = action.payload;
        state.pass = action.payload;
      })
      .addCase(forgotPasswordTok.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;

        state.passResetError = action.payload || "Failed to send reset code";
        state.message = state.passResetError;
        toast.error(state.passResetError);
      })

      /* =========================
         PASSWORD RESET - VERIFY CODE
      ========================= */
      .addCase(verifyResetCodeTok.pending, (state) => {
        state.isLoading = true;
        state.passResetError = "";
        state.verifyResetResult = null;
      })
      .addCase(verifyResetCodeTok.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.verifyResetResult = action.payload;
      })
      .addCase(verifyResetCodeTok.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;

        state.passResetError = action.payload || "Verify reset code failed";
        state.message = state.passResetError;
        toast.error(state.passResetError);
      })

      /* =========================
         PASSWORD RESET - SET NEW PASSWORD
      ========================= */
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.passResetError = "";
        state.passResetResult = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        state.passResetResult = action.payload;
        state.pass = action.payload;
        toast.success("Password reset successful");
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;

        state.passResetError = action.payload || "Failed to reset password";
        state.message = state.passResetError;
        toast.error(state.passResetError);
      })
      /* =========================
          DPO CREATE TOKEN
        ========================= */
        .addCase(createDpoPaymentToken.pending, (state) => {
          state.isLoading = true;
          state.isError = false;
          state.isSuccess = false;
          state.dpoError = "";
          state.dpoTokenResult = null;
          state.dpoPaymentUrl = "";
        })
        .addCase(createDpoPaymentToken.fulfilled, (state, action) => {
          state.isLoading = false;
          state.isError = false;
          state.isSuccess = true;

          state.dpoTokenResult = action.payload;
          state.dpoPaymentUrl = action.payload?.paymentUrl || "";

          try {
            if (action.payload?.token) {
              localStorage.setItem("kupto_dpo_token", action.payload.token);
            }

            if (action.payload?.orderId) {
              localStorage.setItem("kupto_dpo_order_id", action.payload.orderId);
            }
          } catch {}
        })
        .addCase(createDpoPaymentToken.rejected, (state, action) => {
          state.isLoading = false;
          state.isError = true;
          state.isSuccess = false;
          state.dpoError = action.payload || "Failed to start DPO payment";
          state.message = state.dpoError;
          toast.error(state.dpoError);
        })

        /* =========================
          DPO VERIFY TOKEN
        ========================= */
        .addCase(verifyDpoPaymentToken.pending, (state) => {
          state.isLoading = true;
          state.isError = false;
          state.isSuccess = false;
          state.dpoError = "";
          state.dpoVerifyResult = null;
        })
        .addCase(verifyDpoPaymentToken.fulfilled, (state, action) => {
          state.isLoading = false;
          state.isError = false;
          state.isSuccess = true;

          state.dpoVerifyResult = action.payload;

          const updatedOrder = action.payload?.order;

          if (updatedOrder?._id && Array.isArray(state.getOrderedProduct)) {
            state.getOrderedProduct = state.getOrderedProduct.map((o) =>
              o?._id === updatedOrder._id ? updatedOrder : o
            );
          }

          try {
            localStorage.removeItem("kupto_dpo_token");
            localStorage.removeItem("kupto_dpo_order_id");
          } catch {}

          toast.success(action.payload?.message || "Payment verified successfully");
        })
        .addCase(verifyDpoPaymentToken.rejected, (state, action) => {
          state.isLoading = false;
          state.isError = true;
          state.isSuccess = false;

          state.dpoError = action.payload || "Failed to verify DPO payment";
          state.message = state.dpoError;

          toast.error(state.dpoError);
        })

      /* =========================
         ORDERS
      ========================= */
      .addCase(createAnOrder.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createAnOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.createdOrder = action.payload;
      })
      .addCase(createAnOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        toast.error(state.message || "Failed to create order");
      })

      .addCase(getOrders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.getOrderedProduct = Array.isArray(action.payload?.orders)
          ? action.payload.orders
          : [];
      })
      .addCase(getOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
      })

      /* =========================
         CANCEL ORDER
      ========================= */
      .addCase(cancelMyOrder.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(cancelMyOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;

        const updated = action.payload?.order || action.payload;
        state.cancelledOrder = updated || null;

        if (updated?._id && Array.isArray(state.getOrderedProduct)) {
          state.getOrderedProduct = state.getOrderedProduct.map((o) =>
            o?._id === updated._id ? updated : o
          );
        }

        toast.success(action.payload?.message || "Order cancelled");
      })
      .addCase(cancelMyOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload;
        toast.error(state.message || "Failed to cancel order");
      })

      /* =========================
         RESET STATE
      ========================= */
      .addCase(resetState, () => getInitialState());
  },
});

export const {
  clearIdentifyState,
  clearOtpState,
  clearPasswordResetState,
  clearGoogleCompleteProfileState,
  clearDpoState,
} = authSlice.actions;

export default authSlice.reducer;